import { Spinner } from "@fluentui/react";
import { Tooltip } from "@fluentui/react-components";
import styles from "./HistorySidebar.module.css";
import { Answers, HistoryProviderOptions } from "../HistoryProviders/IProvider";
import { useMsal } from "@azure/msal-react";
import { getToken, useLogin } from "../../authConfig";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHistoryManager, HistoryMetaData } from "../HistoryProviders";
import { HistoryData, HistoryItem } from "../HistoryItem";
import { UserProfile } from "../UserProfile";
// import undplogo from "../../assets/undp-logo-blue.png";

import { List24Regular, Edit24Regular } from "@fluentui/react-icons";

const HISTORY_COUNT_PER_LOAD = 20;

export const HistorySidebar = ({
    provider,
    isOpen,
    notify,
    onToggle,
    onNewChat,
    onChatSelected,
    currentChatId
}: {
    provider: HistoryProviderOptions;
    isOpen: boolean;
    notify: boolean;
    onToggle: () => void;
    onNewChat: () => void;
    onChatSelected: (id: string, answers: Answers, conversationType?: string) => void;
    currentChatId?: string;
}) => {
    const historyManager = useHistoryManager(provider);
    const [history, setHistory] = useState<HistoryMetaData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMoreHistory, setHasMoreHistory] = useState(false);

    const client = useLogin ? useMsal().instance : undefined;

    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();

    useEffect(() => {
        if (notify) {
            setHistory([]);
            historyManager.resetContinuationToken();
            setHasMoreHistory(true);
        }
    }, [notify]);

    const loadMoreHistory = async () => {
        setIsLoading(() => true);
        const token = client ? await getToken(client) : undefined;
        const items = await historyManager.getNextItems(HISTORY_COUNT_PER_LOAD, activeAccount?.localAccountId);
        if (items.length === 0) {
            setHasMoreHistory(false);
        }
        setHistory(prevHistory => [...prevHistory, ...items]);
        setIsLoading(() => false);
    };

    const handleSelect = async (item: HistoryData) => {
        const token = client ? await getToken(client) : undefined;
        const answers = await historyManager.getItem(item.id, token, activeAccount?.localAccountId);
        if (answers) {
            onChatSelected(item.id, answers, item.conversation_type);
        }
    };

    const handleDelete = async (id: string) => {
        const token = client ? await getToken(client) : undefined;
        await historyManager.deleteItem(id, token, activeAccount?.localAccountId);
        setHistory(prevHistory => prevHistory.filter(item => item.id !== id));
        if (id === currentChatId) {
            onNewChat();
        }
    };

    const handleRename = async (id: string, title: string) => {
        const token = client ? await getToken(client) : undefined;
        await historyManager.renameItem(id, title, token, activeAccount?.localAccountId);
        setHistory(prevHistory =>
            prevHistory.map(item => {
                if (item.id === id) {
                    return { ...item, title: title };
                }
                return item;
            })
        );
    };

    const groupedHistory = useMemo(() => groupHistory(history), [history]);

    return (
        <div className={`${styles.sidebar} ${isOpen ? styles.open : styles.collapsed}`}>
            <div className={styles.header}>
                <div className={styles.headerTop}>
                    <button className={styles.menuButton} onClick={onToggle} aria-label="Toggle menu">
                        <List24Regular />
                    </button>
                </div>

                {isOpen ? (
                    <button className={styles.newChatButton} onClick={onNewChat} aria-label="New chat">
                        <Edit24Regular />
                        <span>New chat</span>
                    </button>
                ) : (
                    <button className={styles.newChatButtonCollapsed} onClick={onNewChat} aria-label="New chat">
                        <Edit24Regular />
                    </button>
                )}
            </div>

            <div className={styles.content}>
                {isOpen && (
                    <>
                        {Object.entries(groupedHistory).map(([group, items]) => (
                            <div key={group} className={styles.group}>
                                <p className={styles.groupLabel}>{group}</p>
                                {items.map(item => (
                                    <HistoryItem key={item.id} item={item} onSelect={handleSelect} onRename={handleRename} onDelete={handleDelete} />
                                ))}
                            </div>
                        ))}
                        {isLoading && <Spinner style={{ marginTop: "10px" }} />}
                        {history.length === 0 && !isLoading && <p>No History</p>}
                        {hasMoreHistory && !isLoading && <InfiniteLoadingButton func={loadMoreHistory} />}
                    </>
                )}
            </div>

            <div className={styles.footer}>
                <UserProfile />
            </div>
        </div>
    );
};

function groupHistory(history: HistoryData[]) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    const lastMonth = new Date(today);
    lastMonth.setDate(lastMonth.getDate() - 30);
    return history.reduce(
        (groups, item) => {
            const itemDate = new Date(item.timestamp);
            let group;
            if (itemDate >= today) {
                group = "Today";
            } else if (itemDate >= yesterday) {
                group = "Yesterday";
            } else if (itemDate >= lastWeek) {
                group = "Last 7 days";
            } else if (itemDate >= lastMonth) {
                group = "Last 30 days";
            } else {
                group = itemDate.toLocaleDateString(undefined, { year: "numeric", month: "long" });
            }
            if (!groups[group]) {
                groups[group] = [];
            }
            groups[group].push(item);
            return groups;
        },
        {} as Record<string, HistoryData[]>
    );
}

const InfiniteLoadingButton = ({ func }: { func: () => void }) => {
    const buttonRef = useRef(null);
    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        if (buttonRef.current) {
                            func();
                        }
                    }
                });
            },
            {
                root: null,
                threshold: 0
            }
        );
        if (buttonRef.current) {
            observer.observe(buttonRef.current);
        }
        return () => {
            if (buttonRef.current) {
                observer.unobserve(buttonRef.current);
            }
        };
    }, []);
    return <button ref={buttonRef} onClick={func} />;
};
